import "./myBookingsPage.css";
import { useState, useEffect, useContext } from "react";
import usersRequest from "../../request/usersRequest";
import scheduleBasedRequest from "../../request/scheduleBasedRequest";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import MyBookingsPageSkeleton from "./myBookingsPageSkeleton/myBookingsPageSkeleton";
import generalRequest from "../../request/generalRequest";
import BookingModal from "../bookingModal/bookingModal";
import useModalDismiss from "../common/useModalDismiss";

const MyBookingsPage = () => {
    const [bookings, setBookings] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isBookingOpen, setIsBookingOpen] = useState(false);
    const [rescheduleData, setRescheduleData] = useState(null);
    const [shopData, setShopData] = useState(null);
    const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
    const [bookingToCancel, setBookingToCancel] = useState(null);

    useModalDismiss(
        () => {
            setConfirmCancelOpen(false);
            setBookingToCancel(null);
        },
        { enabled: confirmCancelOpen }
    );

    const { userData, language } = useContext(CombinedContext);
    const t = translations[language].myBookings;

    const isLoggedIn = !!(userData && (userData.cognitoTokens?.IdToken || userData.user));
    const userId = userData?.user?.userId;

    const S3_BUCKET = process.env.REACT_APP_IMAGES_BUCKET;
    const S3_REGION = process.env.REACT_APP_APP_REGION;
    const BUCKET_URL = `https://${S3_BUCKET}.s3.${S3_REGION}.amazonaws.com`;

    useEffect(() => {
        async function loadBookings() {
            if (!isLoggedIn || !userId) {
                setLoading(false);
                return;
            }
            try {
                const data = await usersRequest.getUserBookings(userId, userData.cognitoTokens?.AccessToken);
                setBookings(data.bookings || []);
            } catch (err) {
                setError(err.message || t.errors.load);
            } finally {
                setLoading(false);
            }
        }
        loadBookings();
    }, [isLoggedIn, userId, userData?.cognitoTokens?.IdToken]);

    const now = new Date();
    const upcoming = bookings.filter((b) => new Date(b.startTime) > now && b.status === "confirmed");
    const history = bookings.filter((b) => new Date(b.startTime) <= now || b.status !== "confirmed");

    async function handleCancel(bookingId) {
        try {
            await scheduleBasedRequest.cancelBooking(bookingId, userData.cognitoTokens?.IdToken);
            setBookings((prev) => prev.filter((b) => b.bookingId !== bookingId));
        } catch (err) {
            setError(err.message || t.errors.cancel);
        }
    }

    async function confirmCancelBooking() {
        if (!bookingToCancel) return;

        try {
            await scheduleBasedRequest.cancelBooking(bookingToCancel.bookingId, userData.cognitoTokens?.IdToken);

            setBookings((prev) =>
                prev.map((b) => (b.bookingId === bookingToCancel.bookingId ? { ...b, status: "cancelled" } : b))
            );

            setConfirmCancelOpen(false);
            setBookingToCancel(null);
        } catch (err) {
            setError(err.message || t.errors.cancel);
        }
    }

    const reloadBookings = async () => {
        try {
            const data = await usersRequest.getUserBookings(userId, userData.cognitoTokens?.AccessToken);
            setBookings(data.bookings || []);
        } catch (err) {
            setError(err.message || t.errors.reload);
        }
    };

    async function handleReschedule(booking) {
        try {
            const res = await generalRequest.getShopWithstaffsByIdPublic(booking.shopId);

            setShopData(res.shop);
            setRescheduleData(booking);
            setIsBookingOpen(true);
        } catch (err) {
            setError(err.message || t.errors.reschedule);
        }
    }

    if (loading) return <MyBookingsPageSkeleton />;

    return (
        <div className="container sf-mybookings">
            <div className="grid g-2">
                {/* Upcoming */}
                <div className="card">
                    <div className="hd">
                        <div>{t.upcomingTitle}</div>
                    </div>
                    <div className="bd">
                        {error && <div style={{ color: "red" }}>{error}</div>}
                        {!loading && upcoming.length === 0 && <div className="sub">{t.noUpcoming}</div>}
                        {upcoming.map((b) => (
                            <div key={b.bookingId} className="item">
                                <img
                                    className="avatar"
                                    src={
                                        b.shopImages?.[0]
                                            ? b.shopImages[0].startsWith("http")
                                                ? b.shopImages[0]
                                                : `${BUCKET_URL}/${b.shopImages[0]}`
                                            : "https://www.shutterstock.com/image-photo/stylish-vintage-staff-chair-wooden-600nw-569076817.jpg"
                                    }
                                    alt="shop"
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700 }}>
                                        {b.shopName} • {b.serviceNameEn}
                                    </div>
                                    <div className="sub">
                                        {new Date(b.startTime).toLocaleDateString(language, {
                                            weekday: "short",
                                            month: "short",
                                            day: "numeric",
                                        })}
                                        {" • "}
                                        {new Date(b.startTime).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </div>
                                    <div className="chips">
                                        <span className="badge">{t.emailSent}</span>
                                        <span className="badge">{t.addToCalendar}</span>
                                    </div>
                                </div>
                                <div className="row">
								<button
									className="sf-btn-secondary"
									onClick={(e) => {
										e.stopPropagation();
										handleReschedule(b);
									}}
								>

									{translations[language].myBookings.reschedule}
								</button>
                                    <button className="btn ghost" onClick={() => handleCancel(b.bookingId)}>
                                        {t.cancel}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* History */}
                <div className="card">
                    <div className="hd">
                        <div>{t.historyTitle}</div>
                        <div className="sub">{t.historySub}</div>
                    </div>
                    <div className="bd">
                        {history.length === 0 ? (
                            <div className="sub">{t.noHistory}</div>
                        ) : (
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>{t.table.date}</th>
                                        <th>{t.table.service}</th>
                                        <th>{t.table.shop}</th>
                                        <th>{t.table.status}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((h) => (
                                        <tr key={h.bookingId}>
                                            <td>
                                                {new Date(h.startTime).toLocaleDateString(language, {
                                                    month: "short",
                                                    day: "numeric",
                                                })}
                                            </td>
                                            <td>{h.serviceNameEn}</td>
                                            <td>{h.shopName || "—"}</td>
                                            <td>{h.status}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
            {confirmCancelOpen && (
                <div className="sf-mybookings-confirm-overlay">
                    <div className="sf-mybookings-confirm-modal">
                        <h3 className="sf-mybookings-confirm-title">
                            {language === "es" ? "¿Cancelar cita?" : "Cancel booking?"}
                        </h3>

                        <p className="sf-mybookings-confirm-text">
                            {language === "es"
                                ? "Esta acción no se puede deshacer. ¿Seguro que quieres cancelar tu cita?"
                                : "This action cannot be undone. Are you sure you want to cancel this booking?"}
                        </p>

                        <div className="sf-mybookings-confirm-actions">
                            <button
                                className="sf-mybookings-confirm-secondary"
                                onClick={() => {
                                    setConfirmCancelOpen(false);
                                    setBookingToCancel(null);
                                }}
                            >
                                {language === "es" ? "Volver" : "Go back"}
                            </button>

                            <button className="sf-mybookings-confirm-danger" onClick={confirmCancelBooking}>
                                {language === "es" ? "Sí, cancelar" : "Yes, cancel"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isBookingOpen && shopData && rescheduleData && (
                <BookingModal
                    open={isBookingOpen}
                    onClose={() => {
                        setIsBookingOpen(false);
                        setRescheduleData(null);
                        setShopData(null);
                        reloadBookings();
                    }}
                    availabilityId={shopData.shopId}
                    shopData={shopData}
                    initialServiceId={rescheduleData.services?.[0]?.serviceId}
                    initialstaffId={rescheduleData.staffId}
                    mode="reschedule"
                    existingBooking={rescheduleData}
                />
            )}
        </div>
    );
};

export default MyBookingsPage;
